'use client'
import { cn } from '../../helpers/utils.js'
import SwiperBox from '../SwiperBox/index.js'
import type { Img } from '../../types/props.js'
import { withLayout } from '../../shared/Styles.js'
import { Grid, GridItem } from '../../components/grid.js'
import { Picture, Text } from '../../components/index.js'

export type CreativeModuleDataType = {
  img: Img
  mobileImg: Img
  title: string
  description: string
  theme: 'light' | 'dark'
  span: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
}

interface CreativeModuleType extends React.HTMLAttributes<HTMLDivElement> {
  data: {
    shape?: 'rounded' | 'square'
    productsTab: CreativeModuleDataType[]
  }
}

const Item = ({ data, configuration }: { data: CreativeModuleDataType; configuration?: any }) => {
  return (
    <div
      className={cn(
        'creativeModuleItem',
        'text-info-primary aspect-h-[360] aspect-w-[296] relative flex-1 shrink-0 overflow-hidden md:basis-[296px]',
        configuration?.shape === 'rounded' ? 'rounded-2xl' : 'rounded-none',
        {
          'aiui-dark': data.theme === 'dark',
        }
      )}
    >
      <div className="absolute inset-0">
        <Picture
          className="h-full"
          imgClassName="h-full object-cover"
          source={data?.mobileImg?.url || data?.img?.url}
          alt={data?.mobileImg?.alt || data?.img?.alt || ''}
        />
        <div className="z-1 absolute inset-x-0 bottom-4 box-border flex w-full flex-col overflow-hidden px-4">
          <Text html={data?.title} className="graphic-title truncate text-xl font-bold leading-[1.2]" />
          {data?.description && (
            <Text html={data?.description} className="graphic-title line-clamp-2 text-sm font-bold leading-[1.2]" />
          )}
        </div>
      </div>
    </div>
  )
}

const CreativeModule = ({ data, className, ...rest }: CreativeModuleType) => {
  const { productsTab, shape } = data

  return (
    <>
      <div
        {...rest}
        className={cn('creativeModuleBlock', 'laptop:block box-border hidden w-full overflow-hidden', className)}
      >
        <Grid className={cn('tablet:gap-4 grid grid-cols-12 gap-3 ', className)}>
          {productsTab?.map((item, index) => {
            return (
              <GridItem
                key={index}
                span={item.span}
                className={cn(
                  'text-info-primary relative w-full overflow-hidden',
                  shape === 'rounded' ? 'rounded-2xl' : 'rounded-none',
                  'lg-desktop:h-[480px] desktop:h-[384px] laptop:h-[288px]',
                  {
                    'aiui-dark': item.theme === 'dark',
                  }
                )}
              >
                <Picture source={item.img.url} className="size-full" imgClassName="h-full object-cover" />
                <div className="lg-desktop:bottom-8 lg-desktop:px-8 desktop:bottom-6 desktop:px-6 absolute inset-x-0 bottom-4 flex flex-col gap-[6px] overflow-hidden px-4">
                  {item.title && (
                    <Text
                      html={item.title}
                      as="p"
                      className="desktop:text-[32px] laptop:text-2xl line-clamp-2 text-xl font-bold"
                    />
                  )}
                  {item.description && (
                    <Text html={item.description} className="desktop:text-lg line-clamp-2 text-sm font-bold" />
                  )}
                </div>
              </GridItem>
            )
          })}
        </Grid>
      </div>
      <div className="laptop:hidden block w-full">
        <SwiperBox
          id="CreativeModule"
          className={cn('!overflow-visible', className)}
          data={{
            list: productsTab || [],
            configuration: {
              shape,
            },
          }}
          Slide={Item}
          breakpoints={{
            0: {
              spaceBetween: 12,
              freeMode: false,
              slidesPerView: 1,
            },
            374: {
              spaceBetween: 12,
              freeMode: false,
              slidesPerView: 1.2,
            },
            768: {
              spaceBetween: 16,
              freeMode: false,
              slidesPerView: 2.3,
            },
            1024: {
              spaceBetween: 16,
              freeMode: false,
              slidesPerView: 3,
            },
          }}
        />
      </div>
    </>
  )
}

export default withLayout(CreativeModule)
